import consumer from "./consumer"
import {
  bindNotificationSoundUnlock,
  playIncomingNotificationSound
} from "packs/admin-notification-sounds"
import { visitWithUnsavedGuard } from "packs/init-backoffice-unsaved-warn"

const toastStack = () => document.querySelector("[data-admin-notification-toasts]")

const notificationsUrl = () => toastStack()?.dataset?.notificationsUrl

const formatBadge = (count) => (count > 99 ? "99+" : String(count))

const updateBadge = (count) => {
  const badge = document.querySelector("[data-admin-notifications-badge]")
  if (!badge) return

  const n = Number(count) || 0
  if (n <= 0) {
    badge.hidden = true
    badge.textContent = ""
    return
  }

  badge.hidden = false
  badge.textContent = formatBadge(n)
}

const dismissToast = (el) => {
  if (!el || el.dataset.dismissing === "true") return
  el.dataset.dismissing = "true"
  el.classList.add("is-hiding")
  setTimeout(() => el.remove(), 280)
}

const showToast = (notification, destinationUrl = notificationsUrl()) => {
  const stack = toastStack()
  if (!stack || !notification) return

  const toast = document.createElement("article")
  toast.className = "backoffice-notification-toast"
  toast.setAttribute("role", "status")

  const icon = document.createElement("span")
  icon.className = "backoffice-notification-toast__icon"
  icon.innerHTML = notification.icon_html || ""

  const body = document.createElement("div")
  body.className = "backoffice-notification-toast__body"

  const title = document.createElement("p")
  title.className = "backoffice-notification-toast__title"
  title.textContent = notification.title || "Notification"

  const company = document.createElement("p")
  company.className = "backoffice-notification-toast__company"
  company.textContent = notification.company_name || ""

  const close = document.createElement("button")
  close.type = "button"
  close.className = "backoffice-notification-toast__close"
  close.setAttribute("aria-label", "Fermer")
  close.textContent = "×"
  close.addEventListener("click", (event) => {
    event.preventDefault()
    event.stopPropagation()
    dismissToast(toast)
  })

  body.appendChild(title)
  body.appendChild(company)

  toast.appendChild(close)
  if (notification.icon_html) toast.appendChild(icon)
  toast.appendChild(body)

  const url = destinationUrl
  if (url) {
    toast.classList.add("is-clickable")
    toast.tabIndex = 0
    const open = () => {
      visitWithUnsavedGuard(url)
    }
    toast.addEventListener("click", open)
    toast.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        open()
      }
    })
  }

  stack.appendChild(toast)
}

const notificationsList = () => document.querySelector("[data-admin-notifications-list]")

const listFilters = (list) => {
  try {
    return JSON.parse(list.dataset.adminNotificationsFilters || "{}")
  } catch (error) {
    return {}
  }
}

// Une notification arrive toujours en tête de liste : on ne l'insère que si la
// page affichée est la première et que les filtres actifs la laissent passer.
const matchesFilters = (filters, notification) => {
  if ((filters.page || 1) !== 1) return false
  if (Array.isArray(filters.states) && !filters.states.includes("unread")) return false
  if (Array.isArray(filters.categories) && !filters.categories.includes(notification.category)) return false
  if (filters.created_to && new Date(notification.created_at) > new Date(`${filters.created_to}T23:59:59`)) return false

  return true
}

const buildActions = (notification) => {
  const actions = document.createElement("div")
  actions.className = "backoffice-notification-item__actions"

  if (notification.exhibitor_url) {
    const link = document.createElement("a")
    link.className = "backoffice-btn backoffice-btn--ghost"
    link.href = notification.exhibitor_url
    link.target = "_blank"
    link.rel = "noopener"
    link.textContent = "Voir"
    actions.appendChild(link)
  }

  const token = document.querySelector("meta[name='csrf-token']")?.content
  if (notification.read_url && token) {
    const form = document.createElement("form")
    form.className = "button_to"
    form.method = "post"
    form.action = notification.read_url
    form.dataset.adminNotificationToggleForm = "true"
    form.innerHTML = `
      <input type="hidden" name="_method" value="patch">
      <input type="hidden" name="authenticity_token">
      <button type="submit" class="backoffice-btn backoffice-btn--ghost">Marquer lu</button>
    `
    form.querySelector("input[name='authenticity_token']").value = token
    actions.appendChild(form)
  }

  return actions
}

const buildRow = (notification) => {
  const row = document.createElement("li")
  row.className = "backoffice-notification-item is-unread"
  row.dataset.adminNotificationId = notification.id
  row.dataset.adminNotificationToggle = "true"

  const icon = document.createElement("span")
  icon.className = "backoffice-notification-item__icon"
  icon.innerHTML = notification.icon_html || ""

  const main = document.createElement("div")
  main.className = "backoffice-notification-item__main"

  const title = document.createElement("p")
  title.className = "backoffice-notification-item__title"
  title.textContent = notification.title || ""
  const company = document.createElement("span")
  company.className = "backoffice-notification-item__company"
  company.textContent = notification.company_name || ""
  title.appendChild(company)

  const summary = document.createElement("p")
  summary.className = "backoffice-notification-item__summary"
  if (notification.summary_html) {
    summary.innerHTML = notification.summary_html
  } else {
    summary.textContent = notification.summary || ""
  }
  summary.title = notification.summary || ""

  const meta = document.createElement("p")
  meta.className = "backoffice-notification-item__meta"
  meta.textContent = notification.meta || ""

  main.appendChild(title)
  main.appendChild(summary)
  main.appendChild(meta)

  row.appendChild(icon)
  row.appendChild(main)
  row.appendChild(buildActions(notification))

  return row
}

const insertNotification = (notification) => {
  const list = notificationsList()
  if (!list || !notification) return
  if (list.querySelector(`[data-admin-notification-id="${notification.id}"]`)) return
  if (!matchesFilters(listFilters(list), notification)) return

  list.prepend(buildRow(notification))
  list.hidden = false
  document.querySelector("[data-admin-notifications-empty]")?.setAttribute("hidden", "hidden")
}

const updateParticipantsBadge = (count, editionId) => {
  if (String(document.body?.dataset?.backofficeEditionId || "") !== String(editionId || "")) return

  const badge = document.querySelector("[data-participants-nav-badge]")
  if (!badge) return

  const n = Number(count) || 0
  badge.hidden = n <= 0
  badge.textContent = n > 0 ? formatBadge(n) : ""
}

// Le serveur rerend la page courante : on récupère ainsi une ligne strictement
// identique au tableau Rails, et tous les filtres / tris / paginations restent vrais.
const refreshParticipantsList = async (participant, { force = false } = {}) => {
  const root = document.querySelector("[data-participants-live-list]")
  if (!root) return
  if (!force && !participant) return
  if (!force && root.dataset.participantsType !== participant.type) return
  const editionId = participant?.edition_id
  if (editionId && String(root.dataset.participantsEditionId) !== String(editionId)) return

  try {
    const response = await fetch(window.location.href, {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
      cache: "no-store"
    })
    if (!response.ok) return

    const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html")
    const nextRoot = nextDocument.querySelector("[data-participants-live-list]")
    const currentBody = root.querySelector("[data-participants-live-body]")
    const nextBody = nextRoot?.querySelector("[data-participants-live-body]")
    if (!currentBody && nextBody) {
      if (window.Turbolinks) Turbolinks.visit(window.location.href)
      else window.location.reload()
      return
    }
    if (!currentBody || !nextBody) return

    currentBody.replaceWith(nextBody)

    const currentTotal = root.querySelector(".backoffice-participants-total")
    const nextTotal = nextRoot.querySelector(".backoffice-participants-total")
    if (currentTotal && nextTotal) currentTotal.replaceWith(nextTotal)

    const currentReviewButton = root.querySelector(".backoffice-participants-review-cta")
    const nextReviewButton = nextRoot.querySelector(".backoffice-participants-review-cta")
    if (currentReviewButton && nextReviewButton) currentReviewButton.replaceWith(nextReviewButton)
    else if (currentReviewButton && !nextReviewButton) currentReviewButton.remove()
    else if (!currentReviewButton && nextReviewButton) {
      root.querySelector(".backoffice-page__header-right")?.prepend(nextReviewButton)
    }

    const currentReview = document.querySelector("[data-registration-review]")
    const nextReview = nextDocument.querySelector("[data-registration-review]")
    if (currentReview?.hidden && nextReview) {
      currentReview.replaceWith(nextReview)
      document.dispatchEvent(new Event("backoffice:registration-review-refreshed"))
    }
  } catch (_) {
    // Le toast et la pastille restent fonctionnels si le rafraîchissement échoue.
  }
}

const refreshDashboard = async (editionId) => {
  const root = document.querySelector("[data-live-dashboard]")
  if (!root) return
  if (String(root.dataset.liveEditionId || "") !== String(editionId || "")) return

  try {
    const response = await fetch(window.location.href, {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
      cache: "no-store"
    })
    if (!response.ok) return

    const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html")
    const nextRoot = nextDocument.querySelector("[data-live-dashboard]")
    if (nextRoot) root.replaceWith(nextRoot)
  } catch (_) {
    // Le prochain événement temps réel retentera le rafraîchissement.
  }
}

let refreshTimer = null
let refreshEditionId = null

const scheduleAdminPageRefresh = (editionId) => {
  refreshEditionId = editionId
  window.clearTimeout(refreshTimer)
  refreshTimer = window.setTimeout(() => {
    refreshDashboard(refreshEditionId)
    refreshParticipantsList(
      { edition_id: refreshEditionId },
      { force: true }
    )
  }, 150)
}

const participantRoot = () => document.querySelector("[data-live-participant]")

const sameOpenParticipant = (resource) => {
  const root = participantRoot()
  if (!root || !resource) return false
  return root.dataset.resourceType === String(resource.type) &&
    root.dataset.resourceId === String(resource.id)
}

const showStaleParticipantBanner = () => {
  const root = participantRoot()
  const banner = root?.querySelector("[data-live-stale]")
  if (banner) {
    banner.hidden = false
    root.dataset.liveStale = "true"
  }
}

const livePresence = new Map()

const renderPresence = () => {
  const root = participantRoot()
  const banner = root?.querySelector("[data-live-presence]")
  if (!root || !banner) return

  const currentAdminId = root.dataset.currentAdminId
  const now = Date.now()
  const names = []
  livePresence.forEach((entry, key) => {
    if (now - entry.seenAt > 35_000) {
      livePresence.delete(key)
      return
    }
    if (String(entry.id) !== String(currentAdminId)) names.push(entry.name)
  })

  banner.hidden = names.length === 0
  banner.textContent =
    names.length === 1
      ? `${names[0]} est sur cette fiche`
      : names.length > 1
        ? `${names.join(", ")} sont sur cette fiche`
        : ""
}

const receivePresence = (data) => {
  if (!sameOpenParticipant(data.resource) || !data.user) return
  const key = String(data.user.id)
  if (data.active) {
    livePresence.set(key, { ...data.user, seenAt: Date.now() })
  } else {
    livePresence.delete(key)
  }
  renderPresence()
}

let subscription = null
let presenceTimer = null

const sendPresence = (active = true) => {
  const root = participantRoot()
  if (!subscription || !root) return
  subscription.perform(active ? "appear" : "away", {
    resource_type: root.dataset.resourceType,
    resource_id: root.dataset.resourceId
  })
}

const startPresence = () => {
  window.clearInterval(presenceTimer)
  livePresence.clear()
  renderPresence()
  if (!participantRoot()) return
  sendPresence(true)
  presenceTimer = window.setInterval(() => sendPresence(true), 15_000)
}

const stopPresence = () => {
  sendPresence(false)
  window.clearInterval(presenceTimer)
  presenceTimer = null
  livePresence.clear()
}

const handlePayload = (data) => {
  if (!data) return

  if (typeof data.unread_count === "number") {
    updateBadge(data.unread_count)
  }

  if (data.event === "created" && data.notification) {
    insertNotification(data.notification)
    showToast(data.notification)
    playIncomingNotificationSound("change")
    if (
      data.notification.exhibitor_id &&
      sameOpenParticipant({ type: "Exhibitor", id: data.notification.exhibitor_id })
    ) {
      showStaleParticipantBanner()
    }
  }

  if (data.event === "participant_created" && data.participant) {
    updateParticipantsBadge(data.participants_badge_count, data.participant.edition_id)
    refreshParticipantsList(data.participant)
    showToast(data.participant, data.participant.participant_url)
    playIncomingNotificationSound("registration")
  }

  if (data.event === "data_changed" || data.event === "participant_updated") {
    const editionId = data.edition_id
    if (data.pending) updateParticipantsBadge(data.pending.count, editionId)
    scheduleAdminPageRefresh(editionId)

    if (
      data.event === "participant_updated" &&
      sameOpenParticipant(data.resource) &&
      String(data.actor_id || "") !== String(participantRoot()?.dataset.currentAdminId || "")
    ) {
      showStaleParticipantBanner()
    }
  }

  if (data.event === "participant_presence") receivePresence(data)
}

let subscribed = false

document.addEventListener("turbolinks:load", () => {
  if (!document.body?.classList.contains("backoffice-body")) return

  document.querySelector("[data-live-stale-reload]")?.addEventListener("click", () => {
    visitWithUnsavedGuard(window.location.href)
  }, { once: true })

  participantRoot()?.querySelector("form[data-unsaved-warn]")?.addEventListener("submit", (event) => {
    if (participantRoot()?.dataset.liveStale !== "true") return
    event.preventDefault()
    event.stopImmediatePropagation()
    if (window.Swal) {
      window.Swal.fire({
        icon: "warning",
        title: "Cette fiche a changé",
        text: "Actualisez la fiche avant d’enregistrer afin de ne pas écraser les modifications reçues.",
        confirmButtonText: "Compris"
      })
    }
  }, { capture: true })

  if (subscribed) {
    startPresence()
    return
  }

  subscribed = true
  bindNotificationSoundUnlock()
  subscription = consumer.subscriptions.create("AdminNotificationsChannel", {
    connected() {
      startPresence()
    },
    disconnected() {
      window.clearInterval(presenceTimer)
      presenceTimer = null
    },
    received(data) {
      handlePayload(data)
    }
  })
})

document.addEventListener("turbolinks:before-cache", stopPresence)
window.addEventListener("beforeunload", () => sendPresence(false))
window.setInterval(renderPresence, 10_000)
